import type { CSSProperties, ElementType, FC, ReactNode } from 'react'
import type { WordRevealProps } from '@/types'

import {
    Fragment,
    useEffect,
    useRef,
    useState
} from 'react'

import { EASE } from '@/constants/type'

/**
 * Reveals a line of type word by word, each rising out of its own clipping mask.
 * The same weight and easing as the wall's name re-reveal, stretched over a line.
 *
 * Driven by React state and CSS transitions rather than a tween library: the
 * words start hidden, so anything that could fail to run would strand the
 * headline invisible. The full string stays on `aria-label`.
 */
export const WordReveal: FC<WordRevealProps> = ({
    text,
    as = 'span',
    className,
    style,
    delay = 0,
    stagger = 0.055,
    trigger = 'mount',
    variant = 'rise'
}): ReactNode => {
    const scopeRef = useRef<HTMLSpanElement | null>(null)

    const [mounted, setMounted] = useState(false)
    const [seen, setSeen] = useState(false)
    const [reduced, setReduced] = useState(false)

    useEffect(() => {
        setMounted(true)
        setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    }, [])

    useEffect(() => {
        if (trigger !== 'scroll') return

        const scope = scopeRef.current
        if (!scope) return

        const observer = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setSeen(true)
                        observer.disconnect()
                    }
                }
            },
            { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
        )

        observer.observe(scope)

        return () => {
            observer.disconnect()
        }
    }, [trigger])

    const open = reduced ? true : trigger === 'mount' ? mounted : seen
    const words = text.split(' ').filter(Boolean)
    const Tag = as as ElementType

    /* A plain fade needs no masks and no word splitting, so the text stays a
       single real text node — better for selection, wrapping and screen readers. */
    if (variant === 'fade') {
        return (
            <Tag className={className} style={style}>
                <span
                    ref={scopeRef}
                    style={{
                        opacity: open ? 1 : 0,
                        transition: reduced ? 'none' : `opacity 1.1s ${EASE} ${delay}s`
                    }}
                >
                    {text}
                </span>
            </Tag>
        )
    }

    return (
        <Tag className={className} style={style} aria-label={text}>
            {/* Plain inline span: it has to generate line boxes for the observer
                to have anything to intersect with. */}
            <span ref={scopeRef}>
                {words.map((word, index) => {
                    const at = delay + index * stagger

                    const wordStyle: CSSProperties = {
                        display: 'inline-block',
                        opacity: open ? 1 : 0,
                        transform: open ? 'translateY(0)' : 'translateY(118%)',
                        filter: open ? 'blur(0px)' : 'blur(8px)',
                        transition: reduced
                            ? 'none'
                            : `opacity 0.9s ${EASE} ${at}s, transform 0.9s ${EASE} ${at}s, filter 0.9s ${EASE} ${at}s`
                    }

                    return (
                        <Fragment key={`${word}-${index}`}>
                            {/* Padding gives ascenders and descenders room inside
                                the mask; the negative margins keep layout unchanged. */}
                            <span
                                aria-hidden='true'
                                className='inline-block overflow-hidden align-bottom'
                                style={{
                                    paddingTop: '0.16em',
                                    paddingBottom: '0.2em',
                                    marginTop: '-0.16em',
                                    marginBottom: '-0.2em'
                                }}
                            >
                                <span style={wordStyle}>{word}</span>
                            </span>

                            {index < words.length - 1 ? ' ' : null}
                        </Fragment>
                    )
                })}
            </span>
        </Tag>
    )
}
